
import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/auth/AuthContext';
import { UserRoles } from '@/contexts/auth/enums';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAuth?: boolean;
  requiredRole?: UserRoles;
  publicAccessible?: boolean;
  redirectTo?: string;
  onEnter?: () => void;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireAuth = false,
  requiredRole,
  publicAccessible = false,
  redirectTo = '/login',
  onEnter,
}) => {
  const { isAuthenticated, user, isLoading } = useAuth();
  const location = useLocation();

  // Call onEnter callback for analytics
  useEffect(() => {
    if (onEnter) {
      onEnter();
    }
  }, [onEnter, location.pathname]);

  // Check if still loading auth state
  if (isLoading) {
    return <div className="flex items-center justify-center min-h-screen">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-medihub-primary"></div>
    </div>;
  }

  // If authentication is required but user is not authenticated
  if (requireAuth && !isAuthenticated) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // If specific role is required but user doesn't have it
  if (requiredRole && user && !user.roles.includes(requiredRole)) {
    // For admin routes, redirect to home if user doesn't have admin role
    if (requiredRole === UserRoles.ADMIN) {
      return <Navigate to="/" replace />;
    }
    
    // For organizer routes, redirect to profile if user doesn't have organizer role
    if (requiredRole === UserRoles.ORGANIZER) {
      return <Navigate to="/participant/profile" replace />;
    }
  }

  // If route is public and user is authenticated, allow access
  if (publicAccessible || isAuthenticated) {
    return <>{children}</>;
  }

  // Default: redirect to login
  return <Navigate to={redirectTo} state={{ from: location }} replace />;
};

export default ProtectedRoute;
